import React, { useEffect } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { RadioInput } from 'components/form';
import { toTitleCase } from 'utils/titleCase';
import * as yup from 'yup';
import { BaseEditModal } from '../base-edit-modal';

type WorkMotivatorFormData = {
  workMotivators: {
    title: string;
    description: string;
    checked: boolean;
  }[];
};

const workMotivatorSchema = yup.object().shape({
  workMotivators: yup.array().of(
    yup.object().shape({
      title: yup.string().required('Motivator name is required'),
      description: yup.string().required('Description is required'),
      checked: yup.boolean().default(false),
    })
  )
    .required('Work motivators are required')
    .test(
      'exactly-three-checked',
      'Select exactly 3 work motivators before saving',
      (motivators = []) => motivators.filter(motivator => motivator.checked).length === 3
    ),
});

interface WorkMotivatorsEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentData: WorkMotivatorFormData;
  onSave: (data: WorkMotivatorFormData) => void;
  isSubmitting?: boolean;
}

export const WorkMotivatorsEditModal: React.FC<WorkMotivatorsEditModalProps> = ({
  isOpen,
  onClose,
  currentData,
  onSave,
  isSubmitting,
}) => {
  const defaultValues: WorkMotivatorFormData = {
    workMotivators: currentData?.workMotivators || [],
  };

  const {
    register,
    handleSubmit,
    formState: { errors },
    control,
    reset,
    watch,
  } = useForm<WorkMotivatorFormData>({
    defaultValues,
    resolver: yupResolver(workMotivatorSchema),
    mode: 'onChange',
  });

  const { fields } = useFieldArray({
    control,
    name: 'workMotivators',
  });

  useEffect(() => {
    reset(defaultValues);
  }, [currentData, reset]);

  const onSubmit = (data: WorkMotivatorFormData) => {
    onSave(data);
  };

  const watchedMotivators = watch('workMotivators') || [];
  const checkedCount = watchedMotivators.filter(motivator => motivator.checked).length;
  const selectionMessage = checkedCount !== 3 ? 'Select exactly 3 work motivators before saving.' : '';

  return (
    <BaseEditModal
      isOpen={isOpen}
      onClose={onClose}
      title="Work Motivators"
      onSubmit={handleSubmit(onSubmit)}
      isSubmitting={isSubmitting}
    >
      <div className="font-[Gelion] w-[90%] mx-auto mt-[10px] mb-[30px]">
        <p className="mb-[20px] pb-2.5 border-b border-[#D0D5DD]">
          You can select any of the work motivators that best resonate with the role
        </p>
        <h3 className="text-lg font-semibold text-[#1D2939] mb-1">Work Motivators</h3>

        {selectionMessage && (
          <small className="text-xs text-[#e74444] block mb-3">{selectionMessage}</small>
        )}

        {errors.workMotivators && typeof errors.workMotivators === 'object' && 'message' in errors.workMotivators && (
          <small className="text-xs text-[#e74444] block mb-3">{errors.workMotivators.message as string}</small>
        )}

        <div className="grid grid-cols-2 gap-x-[30px] gap-y-[10px]">
          {fields.map((motivator, index) => (
            <div key={motivator.id} className="items-start gap-3">
              <RadioInput
                register={register}
                name={`workMotivators.${index}.checked`}
                validatorMessage={errors.workMotivators?.[index]?.checked?.message}
                options={[{
                  label: toTitleCase(motivator.title),
                  description: motivator.description,
                  value: 'true',
                }]}
                type="checkbox"
                parentClassName="grid gap-[5%] !mb-0"
                labelClassName='!mb-1'
                disabled={checkedCount >= 3 && !watch(`workMotivators.${index}.checked`)}
              />
            </div>
          ))}
        </div>
      </div>
    </BaseEditModal>
  );
};

export default WorkMotivatorsEditModal;
